import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { useMemo } from 'react';
import { z } from 'zod';

import type { LoanModalPayload, ModalProps } from '../types/modal.types';

import { useProcessLateFeeMutation } from '../../__generated__/graphql/api';
import { getLoanRefetchQuery } from '../../components/LoanDetails/getLoanRefetchQuery';
import { createFormComponent } from '../utils/create-form-component';
import { mapToBoolean, YES_NO_OPTIONS } from '../utils/form-helpers';

const schema = z.object({
  dryRun: z.string().optional(),
});

export const ProcessLateFeeComponent = ({ payload, api }: ModalProps<LoanModalPayload>) => {
  const { loanId } = payload;
  const { handleMutation } = useMutationWithToast();
  const refetchQuery = getLoanRefetchQuery(loanId);
  const [processLateFee, { loading }] = useProcessLateFeeMutation(refetchQuery);

  const handleSubmit = (data: z.infer<typeof schema>) => {
    void handleMutation({
      mutation: () =>
        processLateFee({
          variables: {
            loanId,
            dryRun: mapToBoolean(data.dryRun),
          },
        }),
      extractSuccess: (result) => !!result?.data,
      onSuccess: () => api.close(),
      debugLabel: 'process-late-fee',
      toast: {
        success: 'Late fee processed successfully',
        error: 'Failed to process late fee',
        loading: 'Processing late fee...',
      },
    });
  };

  const config: SmartFormConfig<z.infer<typeof schema>> = useMemo(
    () => ({
      fieldGroups: [
        {
          name: 'options',
          label: 'Process Late Fee Options',
          fields: [
            {
              name: 'dryRun',
              label: 'Dry Run',
              type: 'select',
              options: YES_NO_OPTIONS,
              required: false,
            },
          ],
        },
      ],
      schema,
      defaultValues: {},
    }),
    [],
  );

  return createFormComponent({
    config,
    handleSubmit,
    loading,
  });
};
